import { motion } from "framer-motion";
import { useState } from "react";

import { Css } from "~generated/css";

type OptionPreviewImageProps = {
  imageUrl?: string | null;
  optionName: string;
  size: number | { width: string; height: string };
  hideBorder?: boolean;
  /** Defaults to "contain" */
  objectFit?: "contain" | "cover";
};

/**
 * Shows an option image cropped to size and with optionName as a temporary fallback.
 */
export function OptionPreviewImage(props: OptionPreviewImageProps) {
  const { imageUrl, optionName, size, hideBorder = false, objectFit = "contain" } = props;
  const [imageLoading, setImageLoading] = useState(true);
  const sizeCss = typeof size === "object" ? Css.h(size.height).w(size.width).$ : Css.sqPx(size).$;

  return (
    <motion.div
      // NOTE: We're changing the background color to white if we have an image because we're using `object-contain` on the image.
      // Many images are too wide or too tall and we don't want to render a non-white background in that case.
      css={{
        ...Css.df.aic.jcc.oh.fs0.br8.ba.bcGray300.relative.z0.if(!!imageUrl).bgWhite.else.bgLightBlue.$,
        ...Css.if(hideBorder).bNone.br0.$,
        ...sizeCss,
      }}
    >
      <motion.img
        onLoad={() => setImageLoading(false)}
        css={Css.if(!!imageUrl).w100.h100.objectFit(objectFit).else.w50.h50.$}
        src={imageUrl ?? IMAGE_NOT_FOUND_URL}
        alt={optionName}
      />
      {/* Image mask */}
      <motion.div
        initial={{ opacity: 1 }}
        css={Css.absolute.bgWhite.z1.w100.h100.$}
        animate={
          !imageLoading && {
            opacity: 0,
            transition: { duration: 0.3 },
            transitionEnd: { display: "none", zIndex: -1 },
          }
        }
      />
    </motion.div>
  );
}

export function OptionPreviewImageLoader(props: Pick<OptionPreviewImageProps, "size">) {
  const { size } = props;

  const sizeCss = typeof size === "object" ? Css.h(size.height).w(size.width).$ : Css.sqPx(size).$;

  return (
    <div
      css={{
        ...Css.df.aic.jcc.oh.fs0.br8.ba.bcGray300.bgLightBlue.add(
          "animation",
          "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
        ).$,
        ...sizeCss,
      }}
    ></div>
  );
}

export const IMAGE_NOT_FOUND_URL = "/image-not-found.svg";
